import React, { useState } from "react";
import "./CustomSizeForm.css";

const CustomSizeForm = ({ onSave, onCancel }) => {
  const [measurements, setMeasurements] = useState({
    bust: "",
    waist: "",
    hip: "",
    shoulder: "",
    sleeveLength: "",
    dressLength: "",
    armhole: "",
    neckSize: "",
    height: "",
  });

  const [errors, setErrors] = useState({});
  const [showMeasurementGuide, setShowMeasurementGuide] = useState(false);

  // =========================================
  // HANDLE INPUT CHANGE
  // =========================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Only numbers and decimal values
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setMeasurements((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // =========================================
  // VALIDATE FORM
  // =========================================

  const validateForm = () => {
    const newErrors = {};

    Object.keys(measurements).forEach((key) => {
      if (!measurements[key]) {
        newErrors[key] = "Required";
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // =========================================
  // SAVE MEASUREMENTS
  // =========================================

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const finalMeasurements = {
      ...measurements,
      unit: "inches",
    };

    if (onSave) {
      onSave(finalMeasurements);
    }
  };

  // =========================================
  // CLOSE FORM
  // =========================================

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // =========================================
  // MEASUREMENT FIELDS
  // =========================================

  const measurementFields = [
    {
      name: "bust",
      label: "Bust / Chest",
      placeholder: "e.g. 38",
    },
    {
      name: "waist",
      label: "Waist",
      placeholder: "e.g. 32",
    },
    {
      name: "hip",
      label: "Hip",
      placeholder: "e.g. 40",
    },
    {
      name: "shoulder",
      label: "Shoulder",
      placeholder: "e.g. 15.5",
    },
    {
      name: "sleeveLength",
      label: "Sleeve Length",
      placeholder: "e.g. 23.5",
    },
    {
      name: "dressLength",
      label: "Dress Length",
      placeholder: "e.g. 44",
    },
    {
      name: "armhole",
      label: "Armhole",
      placeholder: "e.g. 18",
    },
    {
      name: "neckSize",
      label: "Neck Size",
      placeholder: "e.g. 14",
    },
    {
      name: "height",
      label: "Height",
      placeholder: "e.g. 63",
    },
  ];

  // =========================================
  // MEASUREMENT GUIDE
  // =========================================

  const measurementGuide = [
    {
      title: "Bust / Chest",
      text: "Measure around the fullest part of your bust or chest. Keep the measuring tape straight and comfortably fitted.",
    },
    {
      title: "Waist",
      text: "Measure around the narrowest part of your natural waist. Do not pull the tape too tightly.",
    },
    {
      title: "Hip",
      text: "Measure around the fullest part of your hips and seat while keeping the tape level.",
    },
    {
      title: "Shoulder",
      text: "Measure from one shoulder point to the other shoulder point across the back.",
    },
    {
      title: "Sleeve Length",
      text: "Measure from the shoulder point down along the arm to the required sleeve length.",
    },
    {
      title: "Dress Length",
      text: "Measure from the highest point of the shoulder down to where you want the dress to finish.",
    },
    {
      title: "Armhole",
      text: "Measure around the armhole area where the sleeve joins the body. Keep the tape comfortable.",
    },
    {
      title: "Neck Size",
      text: "Measure around the base of your neck where the neckline will sit. Leave a little comfort space.",
    },
    {
      title: "Height",
      text: "Stand straight without shoes and measure from the top of your head to the floor.",
    },
  ];

  return (
    <div className="custom-size-form-wrapper">

      <div className="custom-size-form-card">

        {/* =========================================
            HEADER
        ========================================= */}

        <div className="custom-size-form-header">

          <div className="custom-size-header-content">

            <h3>
              Custom Size ✨
            </h3>

            <p>
              Enter your measurements so we can make the dress according to
              your measurements.
            </p>

          </div>


          {/* UNIT + CLOSE */}

          <div className="custom-size-header-right">

            <div className="custom-size-unit">

              <span>
                Unit
              </span>

              <strong>
                Inches
              </strong>

            </div>


            {/* CLOSE BUTTON */}

            {onCancel && (
              <button
                type="button"
                className="custom-size-close-btn"
                onClick={handleClose}
                aria-label="Close custom size form"
                title="Close"
              >
                ×
              </button>
            )}

          </div>

        </div>


        {/* =========================================
            HOW TO MEASURE BUTTON
        ========================================= */}

        <button
          type="button"
          className="how-to-measure-btn"
          onClick={() =>
            setShowMeasurementGuide(!showMeasurementGuide)
          }
        >
          📏{" "}
          {showMeasurementGuide
            ? "Hide"
            : "How to Measure"}
        </button>


        {/* =========================================
            MEASUREMENT GUIDE
        ========================================= */}

        {showMeasurementGuide && (

          <div className="measurement-guide">

            <div className="measurement-guide-header">

              <div>

                <h4>
                  How to Measure
                </h4>

                <p>
                  Please use a measuring tape and take your measurements
                  carefully.
                </p>

              </div>


              <button
                type="button"
                className="measurement-guide-close"
                onClick={() =>
                  setShowMeasurementGuide(false)
                }
                aria-label="Close measurement guide"
              >
                ×
              </button>

            </div>


            <div className="measurement-guide-list">

              {measurementGuide.map((item, index) => (

                <div
                  className="measurement-guide-item"
                  key={item.title}
                >

                  <div className="measurement-guide-number">
                    {index + 1}
                  </div>

                  <div>

                    <h5>
                      {item.title}
                    </h5>

                    <p>
                      {item.text}
                    </p>

                  </div>

                </div>

              ))}

            </div>


            <div className="measurement-guide-tip">

              <strong>
                💡 Important Tip
              </strong>

              <p>
                Keep the measuring tape comfortable against your body.
                Do not pull it too tight or leave it too loose.
                For better accuracy, ask someone to help you take the
                measurements.
              </p>

            </div>

          </div>

        )}


        {/* =========================================
            FORM
        ========================================= */}

        <form onSubmit={handleSubmit}>

          <div className="custom-measurement-grid">

            {measurementFields.map((field) => (

              <div
                className="custom-measurement-field"
                key={field.name}
              >

                <label htmlFor={field.name}>

                  {field.label}

                  <span>
                    *
                  </span>

                </label>


                <div className="measurement-input-wrapper">

                  <input
                    id={field.name}
                    type="text"
                    inputMode="decimal"
                    name={field.name}
                    value={measurements[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    autoComplete="off"
                  />

                  <span>
                    in
                  </span>

                </div>


                {errors[field.name] && (

                  <small className="measurement-error">
                    {errors[field.name]}
                  </small>

                )}

              </div>

            ))}

          </div>


          {/* =========================================
              NOTE
          ========================================= */}

          <div className="custom-size-note">

            <strong>
              📏 Measurement Tip
            </strong>

            <p>
              Please enter your measurements carefully. Use a measuring
              tape and keep the tape comfortable, not too tight or too
              loose.
            </p>

          </div>


          {/* =========================================
              ACTION BUTTONS
          ========================================= */}

          <div className="custom-size-form-actions">

            {onCancel && (

              <button
                type="button"
                className="custom-size-cancel-btn"
                onClick={handleClose}
              >
                Cancel
              </button>

            )}

            <button
              type="submit"
              className="custom-size-save-btn"
            >
              Save Measurements
            </button>

          </div>

        </form>

      </div>

    </div>
  );
};

export default CustomSizeForm;
